"use client";

import { SignUpForm } from "./SignUpForm";
import { X } from "lucide-react";
import { useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type AuthModalProps = React.ComponentPropsWithoutRef<'div'> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthSuccess?: () => void;
};

export function AuthModal({
  open,
  onOpenChange,
  onAuthSuccess,
  className,
  ...props
}: AuthModalProps) {
  const { t } = useTranslation();
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm w-full h-full"
      onClick={handleClose}
      {...props}
    >
      <div
        className={cn("relative w-full max-w-md", className)}
        onClick={(e) => e.stopPropagation()}
      >
        <SignUpForm
          variant="dialog"
          onAuthSuccess={onAuthSuccess}
        />
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-4 rounded-full text-white/70 transition-colors hover:text-white hover:bg-white/10"
          aria-label={t("authModal.close", "Close")}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
